import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Paper,
  Grid,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  TextField,
  Chip,
  Alert,
  LinearProgress,
  Tabs,
  Tab,
  Container,
  IconButton,
  AppBar,
  Toolbar,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Stack,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Tooltip,
  styled,
  Avatar,
  Menu,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  ListItemIcon,
  ListItemText,
  List
} from '@mui/material';
import {
  Save as SaveIcon,
  Edit as EditIcon,
  Chat as ChatIcon,
  ArrowBack as ArrowBackIcon,
  Close as CloseIcon,
  Upload as UploadIcon,
  FileUpload as FileUploadIcon,
  UploadFile as UploadFileIcon,
  Article as ArticleIcon,
  Science as ScienceIcon,
  Visibility as PreviewIcon,
  Mic as MicIcon,
  Language as LanguageIcon,
  AttachFile as AttachFileIcon,
  Send as SendIcon,
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  Email as EmailIcon,
  Password as PasswordIcon,
  Smartphone as PhoneIcon,
  Link as UrlIcon,
  CalendarToday as DateIcon,
  Person as PersonIcon,
  Domain as DomainIcon,
  Numbers as NumberIcon,
  PersonOutlined as PersonOutlineIcon,
  VerifiedUser as QualityIcon
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import { EditorView } from '@codemirror/view';
import { EditorState } from '@codemirror/state';
import CodeMirror from '@uiw/react-codemirror';
import { markdown } from '@codemirror/lang-markdown';

import useProject from '../hooks/useProject';
import useDocuments from '../hooks/useDocuments';
import useNotification from '../hooks/useNotification';
import ContentEditor from '../components/prd-workflow/ContentEditor';
import WorkflowChat from '../components/prd-workflow/WorkflowChat';
import Designs from '../components/prd-workflow/steps/Designs';
import UserStories from '../components/prd-workflow/steps/UserStories';
import Architecture from '../components/prd-workflow/steps/Architecture';
import FieldValidation from '../components/prd-workflow/steps/FieldValidation';
import Wireframes from '../components/prd-workflow/steps/Wireframes';
import TechnologyStack from '../components/prd-workflow/steps/TechnologyStack';
import SecurityGuidelines from '../components/prd-workflow/steps/SecurityGuidelines';
import DevGuidelines from '../components/prd-workflow/steps/DevGuidelines';
import APIGuidelines from '../components/prd-workflow/steps/APIGuidelines';
import ProcessFlows from '../components/prd-workflow/steps/ProcessFlows';
import UserRoles from '../components/prd-workflow/steps/UserRoles';
import Features from '../components/prd-workflow/steps/Features';
import DesignGuidelines from '../components/prd-workflow/steps/DesignGuidelines';
import CodeDevelopmentPage from '../components/code/CodeDevelopmentPage';
import BoltStyleCodeDevelopment from '../components/code/BoltStyleCodeDevelopment';
import QualityAnalysis from '../components/prd-workflow/QualityAnalysis';
import SplitViewTDD from '../components/prd-workflow/SplitViewTDD';
import { TDDProvider } from '../context/TDDContext';

// Styled components
const StyledTabs = styled(Tabs)(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.divider}`,
  '& .MuiTabs-indicator': {
    backgroundColor: theme.palette.primary.main,
    height: 3
  },
}));

const StyledTab = styled(Tab)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: theme.typography.fontWeightMedium,
  fontSize: theme.typography.pxToRem(15),
  marginRight: theme.spacing(1),
  '&.Mui-selected': {
    color: theme.palette.primary.main,
    fontWeight: theme.typography.fontWeightBold
  },
  '&.Mui-focusVisible': {
    backgroundColor: theme.palette.action.selected
  },
}));

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

/**
 * PRDWorkflowEdit Component
 * 
 * This component handles editing or chatting about a specific step of the PRD workflow in a new window.
 * It receives parameters via query string: step number and type.
 */
const PRDWorkflowEdit = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { projectId } = useParams();
  const searchParams = new URLSearchParams(location.search);
  const step = parseInt(searchParams.get('step') || '0', 10);
  const type = searchParams.get('type') || '';
  
  // Hooks for data access
  const { project: projectData, refetchProject } = useProject({ projectId });
  const { 
    loading, 
    error, 
    document,
    getDocumentById,
    extractPRD,
    generateFieldValidations,
    generateTechStack,
    generateDevGuidelines,
    generateDesignGuidelines,
    generateSecurityGuidelines,
    generateAPIGuidelines,
    generateFeatures,
    generateUserStories,
    generateFinalPRD,
    updateDocument,
    createDocument
  } = useDocuments();
  const { showNotification } = useNotification();
  
  // Define ALL state variables in one place to avoid hot module reloading issues
  const [localLoading, setLocalLoading] = useState(true);
  const [localError, setLocalError] = useState(null);
  const [project, setProject] = useState(null);
  const [currentTab, setCurrentTab] = useState(0);
  const [activeTab, setActiveTab] = useState(type === 'dev-guidelines' || type === 'tdd-development' ? 'content' : 'quality'); // quality vs content view
  const [content, setContent] = useState('');
  const [contentTitle, setContentTitle] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [originalContent, setOriginalContent] = useState('');
  const [isSaved, setIsSaved] = useState(true);
  const [documentId, setDocumentId] = useState(null);
  const [generating, setGenerating] = useState(false);
  
  // Dialog state
  const [showDefaultValidationsDialog, setShowDefaultValidationsDialog] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [genDialogOpen, setGenDialogOpen] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [showAIChat, setShowAIChat] = useState(false);
  
  // Chat state
  const [chatMessage, setChatMessage] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [isChatTyping, setIsChatTyping] = useState(false);
  
  // Import state
  const [activeStep, setActiveStep] = useState(0);
  const [importFile, setImportFile] = useState(null);
  const [importType, setImportType] = useState('json');
  const [importedContent, setImportedContent] = useState('');
  const [processedContent, setProcessedContent] = useState('');
  const [importError, setImportError] = useState(null);
  
  // Refs
  const fileInputRef = useRef(null);
  
  // Map step numbers to step names and data
  const getStepInfo = useCallback((stepNumber, typeOverride) => {
    // If typeOverride is provided, use it, otherwise use the component's type state
    const typeToUse = typeOverride || type;
    const stepMap = {
      0: { title: 'Extract PRD', description: 'Define product requirements', contentType: 'prd' },
      1: { title: 'Field validations', description: 'Define validation rules', contentType: 'validation' },
      2: { 
        title: typeToUse === 'build' ? 'Build' : 'Technology stack', 
        description: typeToUse === 'build' ? 'Build and create development tasks' : 'Define technologies', 
        contentType: typeToUse === 'build' ? 'codeGeneration' : 'techStack' 
      },
      3: { title: 'Development guidelines', description: 'Define coding standards', contentType: 'devGuidelines' },
      4: { title: 'Design guidelines', description: 'Define design principles', contentType: 'designGuidelines' },
      5: { 
        title: typeToUse === 'wireframes' ? 'Wireframes' : typeToUse === 'field-validation' ? 'Field Validation' : 'Security Guidelines', 
        description: typeToUse === 'wireframes' ? 'Create wireframes' : typeToUse === 'field-validation' ? 'Define validation rules' : 'Define security requirements', 
        contentType: typeToUse === 'wireframes' ? 'wireframes' : typeToUse === 'field-validation' ? 'field-validation' : 'securityGuidelines' 
      },
      6: { 
        title: typeToUse === 'designs' ? 'Designs' : typeToUse === 'wireframes' ? 'Wireframes' : 'API Guidelines', 
        description: typeToUse === 'designs' ? 'Define UI/UX designs' : typeToUse === 'wireframes' ? 'Create wireframes' : 'Define API standards', 
        contentType: typeToUse === 'designs' ? 'designs' : typeToUse === 'wireframes' ? 'wireframes' : 'apiGuidelines' 
      },
      7: { title: 'Features', description: 'Define product features', contentType: 'features' },
      8: { title: 'User stories', description: 'Define user stories', contentType: 'userStories' },
      9: { 
        title: typeToUse === 'architecture' ? 'Architecture' : 'Architecture', 
        description: 'Define system architecture', 
        contentType: typeToUse === 'architecture' ? 'architecture' : 'architecture' 
      },
      10: { title: 'Process flows', description: 'Define workflow processes', contentType: 'flows' },
      11: { title: 'User roles', description: 'Define user roles and permissions', contentType: 'userRoles' },
      12: { title: 'Final PRD', description: 'Compile all sections', contentType: 'finalPRD' },
    };
    
    return stepMap[stepNumber] || { title: 'Unknown Step', description: '', contentType: 'unknown' };
  }, [type]);

  // Stable callbacks for CodeGenerationView
  // Code Development specific state with sample Bolt-style tasks
  const [codeTasks, setCodeTasks] = useState([
    {
      id: 'task-1',
      name: 'Import project',
      description: 'Initialize project structure and basic files',
      status: 'pending',
      currentStep: null
    },
    {
      id: 'task-2', 
      name: 'Install dependencies',
      description: 'Install required npm packages and dependencies',
      status: 'pending',
      currentStep: null
    },
    {
      id: 'task-3',
      name: 'Generate code',
      description: 'Generate application code based on requirements',
      status: 'pending', 
      currentStep: null
    },
    {
      id: 'task-5',
      name: 'Start application',
      description: 'Launch the development server and preview',
      status: 'pending',
      currentStep: null
    }
  ]);
  const [codeGenerationProgress, setCodeGenerationProgress] = useState(0);

  const handleGenerateTasks = useCallback((tasks) => {
    console.log('Tasks generated:', tasks);
    setCodeTasks(tasks);
  }, []);
  
  const handleExecuteTask = useCallback((taskId, result) => {
    console.log('Executing task:', taskId, result);
    // Update task status or handle execution result
    if (result?.success) {
      setCodeTasks(prev => prev.map(task => 
        task.id === taskId ? { ...task, status: 'completed' } : task
      ));
    }
  }, []);
  
  const handleRefreshWorkspace = useCallback((workspace) => {
    console.log('Workspace refreshed:', workspace);
    // Handle workspace updates
  }, []);

  const handleCodeChange = useCallback((filePath, content) => {
    console.log('Code changed:', filePath, 'Size:', content?.length);
    // Handle code changes - could save to server, validate, etc.
  }, []);

  const handleTaskComplete = useCallback((result) => {
    console.log('Task completed:', result);
    handleExecuteTask(result.task_id || result.id, result);
    
    // Show success message or handle completion
    if (result?.success) {
      // Could show a notification or update UI
      console.log('Code generation completed successfully');
    }
  }, [handleExecuteTask]);

  // Memoize devPRD to prevent unnecessary re-renders
  const stableDevPRD = useMemo(() => project || {}, [project?.id, project?.name, project?.description]);
  
  // Load initial data
  useEffect(() => {
    let isMounted = true;
    const abortController = new AbortController();
    
    const loadData = async () => {
      try {
        setLocalLoading(true);
        setLocalError(null);
        
        // Get the project data
        const result = await refetchProject();
        if (!isMounted) return;
        
        const freshProjectData = result.data;
        if (!freshProjectData) {
          throw new Error('Project not found');
        }
        
        setProject(freshProjectData);
        
        // Get the document if available
        let docContent = '';
        let docTitle = getStepInfo(step, type).title;
        
        if (freshProjectData.documents && freshProjectData.documents.length > 0) {
          // Find document matching the content type
          const contentType = getStepInfo(step, type).contentType;
          const matchingDoc = freshProjectData.documents.find(doc => doc.type === contentType);
          
          if (matchingDoc && matchingDoc._id) {
            const docData = await getDocumentById(matchingDoc._id);
            if (docData && docData.content) {
              docContent = docData.content;
              setDocumentId(docData._id);
            }
          }
        }
        
        // Set content and title - make sure we only do this once when the component mounts
        if (isMounted) {
          setContent(docContent);
          setOriginalContent(docContent);
          setContentTitle(docTitle);
          setLocalLoading(false);
        }
      } catch (err) {
        if (!isMounted) return;
        console.error('Error loading data:', err);
        setLocalError(err.message || 'Failed to load data');
        setLocalLoading(false);
      }
    };
    
    loadData();
    
    return () => {
      isMounted = false;
      abortController.abort();
    };
  }, [projectId, step, type]);
  
  // Save handler
  const handleSave = async () => {
    try {
      setLocalLoading(true);
      
      const contentType = getStepInfo(step, type).contentType;
      const title = getStepInfo(step, type).title;
      
      // Prepare document data
      const docData = {
        title,
        content,
        type: contentType,
        projectId
      };
      
      // Update existing document or create new
      let result;
      if (documentId) {
        // Update existing document
        result = await updateDocument(documentId, docData);
      } else {
        // Create new document through the appropriate function based on step
        switch (step) {
          case 0:
            result = await extractPRD(projectId, { source: 'manual', content });
            break;
          case 1:
            result = await generateFieldValidations(projectId, '', content);
            break;
          case 2:
            result = await generateTechStack(projectId, content);
            break;
          // Add other cases as needed
          default:
            // Generic document creation
            result = await createDocument(docData);
        }
      }
      
      setLocalLoading(false);
      setIsSaved(true);
      setOriginalContent(content);
      
      if (result && result._id) {
        setDocumentId(result._id);
      }
      
      showNotification({
        message: 'Content saved successfully',
        type: 'success'
      });
      
      // Close the window after successful save
      window.close();
      
    } catch (err) {
      console.error('Error saving content:', err);
      setLocalLoading(false);
      showNotification({
        message: `Error: ${err.message || 'Failed to save content'}`,
        type: 'error'
      });
    }
  };
  
  // Content change handler
  const handleContentChange = (newContent) => {
    setContent(newContent);
    setIsSaved(newContent === originalContent);
  };
  
  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };
  
  // Close window
  const handleClose = () => {
    if (!isSaved) {
      if (window.confirm('You have unsaved changes. Are you sure you want to close this window?')) {
        window.close();
      }
    } else {
      window.close();
    }
  };
  
  // Handle file attachment for wireframes
  const [wireframeAttachments, setWireframeAttachments] = useState([]);

  // Handle file upload for wireframes
  const handleFileUpload = (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const newAttachments = Array.from(files).map(file => {
      return {
        name: file.name,
        type: file.type,
        size: file.size,
        file: file
      };
    });

    setWireframeAttachments(prev => [...prev, ...newAttachments]);
    
    // Show notification
    showNotification({
      message: `${files.length} file(s) attached`,
      type: "success"
    });
  };

  // Handle file drag and drop
  const [isDragging, setIsDragging] = useState(false);
  
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };
  
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };
  
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragging) setIsDragging(true);
  };
  
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (!files || files.length === 0) return;
    
    const newAttachments = Array.from(files).map(file => {
      return {
        name: file.name,
        type: file.type,
        size: file.size,
        file: file
      };
    });
    
    setWireframeAttachments(prev => [...prev, ...newAttachments]);
    
    // Show notification
    showNotification({
      message: `${files.length} file(s) attached`,
      type: "success"
    });
  };

  // Remove an attachment
  const handleRemoveAttachment = (indexToRemove) => {
    setWireframeAttachments(prev => prev.filter((_, index) => index !== indexToRemove));
  };

  // Handle sending chat message
  const handleSendMessage = () => {
    if (!chatMessage.trim() || isChatTyping) return;
    
    // Add user message to chat history
    const userMessage = {
      role: 'user',
      content: chatMessage,
      timestamp: new Date().toISOString()
    };
    
    setChatHistory(prevHistory => [...prevHistory, userMessage]);
    setChatMessage(''); // Clear input
    setIsChatTyping(true); // Show typing indicator
    
    // Different behavior based on content type
    if (type === 'wireframes') {
      // Call API to generate wireframe
      try {
        // Process attachments if any
        const processAttachments = async () => {
          const processedAttachments = [];
          
          for (const attachment of wireframeAttachments) {
            // Read the file content
            const fileReader = new FileReader();
            const fileContent = await new Promise((resolve, reject) => {
              fileReader.onload = (e) => resolve(e.target.result);
              fileReader.onerror = reject;
              
              // Read as text or data URL based on file type
              if (attachment.type.startsWith('text/') || 
                  attachment.type === 'application/json' ||
                  attachment.type === 'application/xml') {
                fileReader.readAsText(attachment.file);
              } else if (attachment.type.startsWith('image/')) {
                fileReader.readAsDataURL(attachment.file);
              } else {
                // For other file types, just include metadata
                resolve(null);
              }
            });
            
            processedAttachments.push({
              name: attachment.name,
              type: attachment.type,
              size: attachment.size,
              content: fileContent
            });
          }
          
          return processedAttachments;
        };
        
        // Generate wireframe using backend API with streaming support
        const generateWireframe = async () => {
          // Define progressInterval in the scope where it can be accessed by both try and catch blocks
          let progressInterval = null;
          
          try {
            // Process any attachments
            const attachments = await processAttachments();
            
            // Add attachment info to the chat message
            if (attachments.length > 0) {
              const attachmentMessage = {
                role: 'user',
                content: `[Attached ${attachments.length} file(s): ${attachments.map(a => a.name).join(', ')}]`,
                timestamp: new Date().toISOString(),
                isAttachment: true
              };
              setChatHistory(prevHistory => [...prevHistory, attachmentMessage]);
            }
            
            // Add a "thinking" message to the chat
            const thinkingMessage = {
              role: 'assistant',
              content: "I'm generating your wireframe based on your requirements. This may take up to a minute...",
              timestamp: new Date().toISOString(),
              isThinking: true,
              progress: 0
            };
            
            setChatHistory(prevHistory => [...prevHistory, thinkingMessage]);
            
            // Initialize content stream for the wireframe with basic HTML structure
            // This will allow us to show the wireframe being built gradually
            let wireframeHtml = `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Generating Wireframe...</title>
    <style>
        body {
            font-family: Arial, sans-serif;
            margin: 0;
            padding: 20px;
            background-color: #f8f9fa;
            color: #333;
        }
        .building-indicator {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            padding: 10px;
            background-color: rgba(25, 118, 210, 0.1);
            color: #1976d2;
            text-align: center;
            font-weight: bold;
            z-index: 1000;
            border-bottom: 1px solid rgba(25, 118, 210, 0.3);
        }
        .progress-bar {
            height: 4px;
            background-color: #1976d2;
            width: 0%;
            transition: width 0.5s ease;
        }
    </style>
</head>
<body>
    <div class="building-indicator">
        <div class="progress-bar"></div>
        <p>Building wireframe... Please wait.</p>
    </div>
    <div id="wireframe-content">
        <h1>Generating your wireframe...</h1>
        <p>The content will appear here as it's being created.</p>
    </div>
</body>
</html>`;
            
            // Set initial content to show the loading wireframe
            setContent(wireframeHtml);
            
            // Update the progress more frequently to simulate streaming better
            let progress = 0;
            progressInterval = setInterval(() => {
              // Increment by smaller amounts for smoother animation
              progress += 5;
              if (progress > 95) {
                if (progressInterval) {
                  clearInterval(progressInterval);
                }
                return;
              }
              
              // Update the progress in both the chat message and wireframe preview
              setChatHistory(prevHistory => {
                const newHistory = [...prevHistory];
                // Find the thinking message
                const thinkingIndex = newHistory.findIndex(msg => msg.isThinking === true);
                if (thinkingIndex !== -1) {
                  // Update the message with new progress and dynamic text
                  let statusText = "I'm generating your wireframe based on your requirements.";
                  if (progress < 30) {
                    statusText = "Analyzing your request and planning the wireframe structure...";
                  } else if (progress < 60) {
                    statusText = "Creating HTML components for the wireframe...";
                  } else if (progress < 90) {
                    statusText = "Finalizing styles and responsive design...";
                  } else {
                    statusText = "Almost done! Preparing wireframe for preview...";
                  }
                  
                  newHistory[thinkingIndex] = {
                    ...newHistory[thinkingIndex],
                    content: statusText,
                    progress: progress
                  };
                }
                return newHistory;
              });
              
              // Update the wireframe preview with progress
              const updatedHtml = wireframeHtml.replace(
                '<div class="progress-bar"></div>',
                `<div class="progress-bar" style="width: ${progress}%"></div>`
              );
              
              // Also add some content based on the progress stage
              let stageContent = '';
              // Always show at least the starting elements
              stageContent += `
              <div style="margin-top: 20px; padding: 20px; background: white; border-radius: 8px; box-shadow: 0 1px 3px rgba(0,0,0,0.1);">
                <h2>Initializing wireframe...</h2>
                <div style="height: 20px; background-color: #e9ecef; border-radius: 4px; margin-bottom: 10px; width: 60%;"></div>
                <div style="height: 10px; background-color: #e9ecef; border-radius: 4px; margin-bottom: 8px; width: 100%;"></div>
                <div style="height: 10px; background-color: #e9ecef; border-radius: 4px; width: 80%;"></div>
              </div>`;
              
              if (progress >= 25) {
                stageContent += `
                <div style="margin-top: 20px; padding: 20px; background: white; border-radius: 8px; box-shadow: 0 1px 3px rgba(0,0,0,0.1);">
                  <h2>Adding layout structure...</h2>
                  <div style="display: flex; justify-content: space-between; margin-top: 20px;">
                    <div style="width: 30%; height: 100px; background-color: #e9ecef; border-radius: 4px;"></div>
                    <div style="width: 65%; height: 100px; background-color: #e9ecef; border-radius: 4px;"></div>
                  </div>
                </div>`;
              }
              
              if (progress >= 50) {
                stageContent += `
                <div style="margin-top: 20px; padding: 20px; background: white; border-radius: 8px; box-shadow: 0 1px 3px rgba(0,0,0,0.1);">
                  <h2>Adding navigation elements...</h2>
                  <div style="display: flex; gap: 10px; margin-top: 10px;">
                    <div style="padding: 8px 16px; background-color: #007bff; color: white; border-radius: 4px;">Home</div>
                    <div style="padding: 8px 16px; background-color: #6c757d; color: white; border-radius: 4px;">Features</div>
                    <div style="padding: 8px 16px; background-color: #6c757d; color: white; border-radius: 4px;">Pricing</div>
                    <div style="padding: 8px 16px; background-color: #6c757d; color: white; border-radius: 4px;">About</div>
                  </div>
                </div>`;
              }
              
              if (progress >= 70) {
                stageContent += `
                <div style="margin-top: 20px; padding: 20px; background: white; border-radius: 8px; box-shadow: 0 1px 3px rgba(0,0,0,0.1);">
                  <h2>Adding content sections...</h2>
                  <div style="margin-top: 20px;">
                    <div style="height: 30px; background-color: #e9ecef; border-radius: 4px; margin-bottom: 10px; width: 80%;"></div>
                    <div style="height: 10px; background-color: #e9ecef; border-radius: 4px; margin-bottom: 8px; width: 100%;"></div>
                    <div style="height: 10px; background-color: #e9ecef; border-radius: 4px; margin-bottom: 8px; width: 95%;"></div>
                    <div style="height: 10px; background-color: #e9ecef; border-radius: 4px; margin-bottom: 8px; width: 98%;"></div>
                    <div style="height: 10px; background-color: #e9ecef; border-radius: 4px; width: 60%;"></div>
                  </div>
                </div>`;
              }
              
              // Update the content in the wireframe
              const contentUpdatedHtml = updatedHtml.replace(
                '<div id="wireframe-content">',
                `<div id="wireframe-content">`
              ).replace(
                '<h1>Generating your wireframe...</h1>',
                `<h1>Building wireframe for ${type} (${progress}% complete)</h1>`
              ).replace(
                '<p>The content will appear here as it\'s being created.</p>',
                `<p>Components are being added as the wireframe is generated.</p>${stageContent}`
              );
              
              // Update the wireframe preview
              setContent(contentUpdatedHtml);
              
            }, 1000); // Update every second for smoother animation
            
            // Start with an empty content string that we'll build up as we receive chunks
            let wireframeHtmlContent = '';
            let receivedAnyContent = false;
            let htmlStarted = false;
            
            // Create a unique key for the wireframe
            const wireframeKey = `wireframe-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
            
            // Import document service to use the streaming function
            const documentService = await import('../services/documentService').then(module => module.default);
            
            // Call the streaming API to generate wireframes
            const response = await documentService.generateWireframeStream(projectId, chatMessage, attachments);
            
            if (!response.ok) {
              if (progressInterval) {
                clearInterval(progressInterval);
                progressInterval = null;
              }
              
              // Handle API error in chat instead of throwing
              const errorMessage = `Failed to generate wireframe: ${response.status} ${response.statusText}`;
              console.error(errorMessage);
              
              // Update the chat with error message but keep chat open
              setChatHistory(prevHistory => {
                const newHistory = [...prevHistory];
                // Find the thinking message
                const thinkingIndex = newHistory.findIndex(msg => msg.isThinking === true);
                if (thinkingIndex !== -1) {
                  newHistory[thinkingIndex] = {
                    role: 'assistant',
                    content: `❌ I encountered an error: ${errorMessage}. Would you like to try again with a different prompt?`,
                    timestamp: new Date().toISOString(),
                    isThinking: false,
                    isError: true
                  };
                } else {
                  // Add a new message if no thinking message was found
                  newHistory.push({
                    role: 'assistant',
                    content: `❌ I encountered an error: ${errorMessage}. Would you like to try again with a different prompt?`,
                    timestamp: new Date().toISOString(),
                    isError: true
                  });
                }
                return newHistory;
              });
              
              // Set chat as no longer typing, but keep the chat open
              setIsChatTyping(false);
              
              // Return early to avoid further processing
              return;
            }
            
            // Clear the progress interval - we'll use the actual streaming response instead
            if (progressInterval) {
              clearInterval(progressInterval);
              progressInterval = null;
            }
            
            // Process the streaming response
            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            
            // Reset wireframeHtmlContent
            wireframeHtmlContent = '';
            let finalHtmlContent = '';
            let finalSectionId = '';
            
            try {
              let done = false;
              
              while (!done) {
                const { value, done: doneReading } = await reader.read();
                done = doneReading;
                
                if (done) break;
                
                const textChunk = decoder.decode(value, { stream: true });
                const lines = textChunk.split('\n').filter(line => line.trim() !== '');
                
                for (const line of lines) {
                  try {
                    const json = JSON.parse(line);
                    
                    // Check what we received in the streaming response
                    if (json.status === 'streaming') {
                      // Update progress and HTML content
                      const progress = json.progress || 0;
                      
                      // Update the progress in the chat message
                      setChatHistory(prevHistory => {
                        const newHistory = [...prevHistory];
                        const thinkingIndex = newHistory.findIndex(msg => msg.isThinking === true);
                        if (thinkingIndex !== -1) {
                          newHistory[thinkingIndex] = {
                            ...newHistory[thinkingIndex],
                            content: json.message || "Generating wireframe...",
                            progress: progress
                          };
                        }
                        return newHistory;
                      });
                      
                      // Update the wireframe preview if HTML is provided
                      if (json.html) {
                        // Debug log to confirm HTML content is received
                        console.log('Received HTML content length:', json.html.length);
                        // Force update the content state
                        setContent(prevContent => {
                          console.log('Updating content, previous length:', prevContent?.length || 0);
                          return json.html;
                        });
                      }
                    } 
                    else if (json.status === 'completed') {
                      // Final wireframe is ready
                      receivedAnyContent = true;
                      wireframeHtmlContent = json.html;
                      finalHtmlContent = json.html;
                      finalSectionId = json.sectionId || wireframeKey;
                      
                      // Debug log
                      console.log('COMPLETED: Final HTML content length:', json.html.length);
                      
                      // Ensure the content is updated with the final version
                      setContent(json.html);
                      
                      // Update the chat with success message but DO NOT close the chat
                      setChatHistory(prevHistory => {
                        const newHistory = [...prevHistory];
                        // Find the thinking message and replace it with completion message
                        const thinkingIndex = newHistory.findIndex(msg => msg.isThinking === true);
                        if (thinkingIndex !== -1) {
                          newHistory[thinkingIndex] = {
                            role: 'assistant',
                            content: "✅ Wireframe generated successfully! I've created a wireframe based on your requirements. You can see it in the preview panel on the right. Feel free to ask for any changes or improvements!",
                            timestamp: new Date().toISOString(),
                            isThinking: false,
                            progress: 100
                          };
                        } else {
                          // Add a new message if no thinking message was found
                          newHistory.push({
                            role: 'assistant',
                            content: "✅ Wireframe generated successfully! I've created a wireframe based on your requirements. You can see it in the preview panel on the right. Feel free to ask for any changes or improvements!",
                            timestamp: new Date().toISOString()
                          });
                        }
                        return newHistory;
                      });

                      // Set chat as no longer typing, but keep the chat open
                      setIsChatTyping(false);
                    }
                    else if (json.status === 'error') {
                      // Handle error in a user-friendly way
                      console.error('Error from wireframe API:', json.message);
                      
                      // Update the chat with error message but keep chat open
                      setChatHistory(prevHistory => {
                        const newHistory = [...prevHistory];
                        // Find the thinking message
                        const thinkingIndex = newHistory.findIndex(msg => msg.isThinking === true);
                        if (thinkingIndex !== -1) {
                          newHistory[thinkingIndex] = {
                            role: 'assistant',
                            content: `❌ I encountered an error while generating the wireframe: ${json.message || 'Unknown error'}. Would you like to try again with a different prompt?`,
                            timestamp: new Date().toISOString(),
                            isThinking: false,
                            isError: true
                          };
                        } else {
                          // Add a new message if no thinking message was found
                          newHistory.push({
                            role: 'assistant',
                            content: `❌ I encountered an error while generating the wireframe: ${json.message || 'Unknown error'}. Would you like to try again with a different prompt?`,
                            timestamp: new Date().toISOString(),
                            isError: true
                          });
                        }
                        return newHistory;
                      });
                      
                      // Set chat as no longer typing, but keep the chat open
                      setIsChatTyping(false);
                    }
                  } catch (e) {
                    console.error('Error parsing JSON from stream:', e, line);
                  }
                }
              }
            } catch (e) {
              console.error('Error reading stream:', e);
              
              // Update the chat with error message but keep chat open
              setChatHistory(prevHistory => {
                const newHistory = [...prevHistory];
                // Find the thinking message
                const thinkingIndex = newHistory.findIndex(msg => msg.isThinking === true);
                if (thinkingIndex !== -1) {
                  newHistory[thinkingIndex] = {
                    role: 'assistant',
                    content: `❌ I encountered an error while processing the wireframe: ${e.message || 'Unknown error'}. Would you like to try again?`,
                    timestamp: new Date().toISOString(),
                    isThinking: false,
                    isError: true
                  };
                } else {
                  // Add a new message if no thinking message was found
                  newHistory.push({
                    role: 'assistant',
                    content: `❌ I encountered an error while processing the wireframe: ${e.message || 'Unknown error'}. Would you like to try again?`,
                    timestamp: new Date().toISOString(),
                    isError: true
                  });
                }
                return newHistory;
              });
              
              // Set chat as no longer typing, but keep the chat open
              setIsChatTyping(false);
            }
            
            // Add a small animation effect to show the wireframe is complete
            // Replace the progress bar with a complete indicator
            let finalHtml = finalHtmlContent || wireframeHtmlContent;
            
            // Store the wireframe key for later saving
            window.wireframeKey = finalSectionId || wireframeKey;
            
            // If somehow we didn't get valid HTML from the API, keep our streaming preview
            if (!finalHtml.startsWith('<!DOCTYPE html>') && !finalHtml.startsWith('<html')) {
              console.warn('Received non-HTML content for wireframe, using streaming preview instead');
              
              // Use our streaming HTML but mark it as 100% complete
              finalHtml = wireframeHtml.replace(
                '<div class="progress-bar"></div>',
                `<div class="progress-bar" style="width: 100%;"></div>`
              ).replace(
                '<p>Building wireframe... Please wait.</p>',
                `<p>Wireframe build complete!</p>`
              ).replace(
                '<h1>Generating your wireframe...</h1>',
                `<h1>Wireframe Generated (100% complete)</h1>`
              );
            }
            
            // Update the UI with the final wireframe content
            setContent(finalHtml);
            
            // Add AI response to chat history - replace the thinking message
            setChatHistory(prevHistory => {
              const newHistory = [...prevHistory];
              // Find and replace the thinking message
              const thinkingIndex = newHistory.findIndex(msg => msg.isThinking === true);
              if (thinkingIndex !== -1) {
                newHistory[thinkingIndex] = {
                  role: 'assistant',
                  content: "✅ Wireframe generation complete! I've created a wireframe based on your requirements. You can view it in the preview panel and save it to your project.",
                  timestamp: new Date().toISOString()
                };
              } else {
                // If we didn't find a thinking message, just add a new one
                newHistory.push({
                  role: 'assistant',
                  content: "✅ Wireframe generation complete! I've created a wireframe based on your requirements. You can view it in the preview panel and save it to your project.",
                  timestamp: new Date().toISOString()
                });
              }
              return newHistory;
            });
            
            setIsChatTyping(false);
            // No need to set document ID as we're not auto-saving anymore
            // Keep the chat open to allow further conversation
            // setShowAIChat(false); - DO NOT close the chat
            setIsSaved(false); // Not saved until user explicitly saves it
            
            // Clear attachments after successful generation
            setWireframeAttachments([]);
            
            showNotification({
              message: "Wireframe generated successfully",
              type: "success"
            });
          } catch (error) {
            // Make sure to clear the interval in case of errors
            if (progressInterval) {
              clearInterval(progressInterval);
              progressInterval = null;
            }
            console.error('Error generating wireframe:', error);
            
            // Add error message to chat history
            setChatHistory(prevHistory => {
              const newHistory = [...prevHistory];
              // Find and replace the thinking message if it exists
              const thinkingIndex = newHistory.findIndex(msg => msg.isThinking === true);
              if (thinkingIndex !== -1) {
                newHistory[thinkingIndex] = {
                  role: 'assistant',
                  content: `I'm sorry, I couldn't generate the wireframe. Error: ${error.message}`,
                  timestamp: new Date().toISOString()
                };
              } else {
                // If we didn't find a thinking message, just add a new one
                newHistory.push({
                  role: 'assistant',
                  content: `I'm sorry, I couldn't generate the wireframe. Error: ${error.message}`,
                  timestamp: new Date().toISOString()
                });
              }
              return newHistory;
            });
            
            setIsChatTyping(false);
            
            showNotification({
              message: `Error generating wireframe: ${error.message}`,
              type: "error"
            });
          }
        };
        
        // Execute the async function
        generateWireframe();
      } catch (error) {
        console.error('Error in wireframe generation:', error);
        setIsChatTyping(false);
        
        // Add error message to chat history
        setChatHistory(prevHistory => {
          const newHistory = [...prevHistory];
          // Add a new error message
          newHistory.push({
            role: 'assistant',
            content: `❌ Something went wrong while trying to generate the wireframe: ${error.message}. Please try again or ask for help.`,
            timestamp: new Date().toISOString(),
            isError: true
          });
          return newHistory;
        });
        
        showNotification({
          message: `Error: ${error.message}`,
          type: "error"
        });
      }
    } else if (step === 1) {
      // Simulate validation rules generation for field validations
      setTimeout(() => {
        const aiResponseContent = `Based on your requirements, here are some validation rules I recommend:

## Email
**Type:** string
**Required:** Yes

### Validation Rules
- **Pattern:** ^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$
- **Min Length:** 5
- **Max Length:** 254

## Password
**Type:** string
**Required:** Yes

### Validation Rules
- **Min Length:** 8
- **Max Length:** 128
- **Pattern:** ^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d)(?=.*[@$!%*?&])[A-Za-z\\d@$!%*?&]{8,}$
- **Description:** Must contain at least one uppercase letter, one lowercase letter, one number, and one special character

Would you like me to continue with more field validations? Or do you want to add these to your validation rules?`;
        
        // Add AI response to chat history
        const aiMessage = {
          role: 'assistant',
          content: aiResponseContent,
          timestamp: new Date().toISOString()
        };
        
        setChatHistory(prevHistory => [...prevHistory, aiMessage]);
        setIsChatTyping(false);
      }, 2000);
    } else {
      // Generic response for other document types
      setTimeout(() => {
        const aiResponseContent = `I've analyzed your request and here's my response for the ${getStepInfo(step, type).title} section. Would you like me to elaborate on any specific part?`;
        
        // Add AI response to chat history
        const aiMessage = {
          role: 'assistant',
          content: aiResponseContent,
          timestamp: new Date().toISOString()
        };
        
        setChatHistory(prevHistory => [...prevHistory, aiMessage]);
        setIsChatTyping(false);
      }, 2000);
    }
  };
  
  // Function to generate wireframe HTML based on user request
  const generateWireframeHtml = (request) => {
    // This is a simplified HTML wireframe template
    // In a real implementation, this would come from the backend API
    return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>AI Generated Wireframe</title>
  <style>
    body {
      font-family: Arial, sans-serif;
      margin: 0;
      padding: 0;
      color: #333;
    }
    
    .container {
      max-width: 1200px;
      margin: 0 auto;
      padding: 20px;
    }
    
    header {
      background-color: #f8f9fa;
      padding: 15px 0;
      border-bottom: 1px solid #e9ecef;
      margin-bottom: 30px;
    }
    
    nav {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
    
    .logo {
      font-weight: bold;
      font-size: 24px;
      color: #333;
    }
    
    .nav-links {
      display: flex;
      gap: 20px;
    }
    
    .nav-link {
      color: #6c757d;
      text-decoration: none;
      padding: 5px 10px;
    }
    
    .nav-link.active {
      color: #007bff;
      border-bottom: 2px solid #007bff;
    }
    
    .button {
      background-color: #007bff;
      color: white;
      border: none;
      padding: 10px 20px;
      border-radius: 4px;
      cursor: pointer;
    }
    
    .hero {
      background-color: #e9ecef;
      padding: 60px 20px;
      text-align: center;
      margin-bottom: 40px;
    }
    
    .hero h1 {
      font-size: 36px;
      margin-bottom: 20px;
    }
    
    .hero p {
      font-size: 18px;
      color: #6c757d;
      max-width: 600px;
      margin: 0 auto 30px;
    }
    
    .features {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 30px;
      margin-bottom: 60px;
    }
    
    .feature {
      background-color: #f8f9fa;
      padding: 30px;
      border-radius: 8px;
      text-align: center;
    }
    
    .feature-icon {
      width: 60px;
      height: 60px;
      background-color: #e9ecef;
      border-radius: 50%;
      margin: 0 auto 20px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 24px;
    }
    
    .feature h3 {
      margin-bottom: 15px;
    }
    
    .feature p {
      color: #6c757d;
    }
    
    form {
      max-width: 500px;
      margin: 0 auto;
    }
    
    .form-group {
      margin-bottom: 20px;
    }
    
    label {
      display: block;
      margin-bottom: 5px;
      font-weight: bold;
    }
    
    input, textarea {
      width: 100%;
      padding: 10px;
      border: 1px solid #ced4da;
      border-radius: 4px;
    }
    
    footer {
      background-color: #343a40;
      color: white;
      padding: 40px 0;
      text-align: center;
    }
  </style>
</head>
<body>
  <header>
    <div class="container">
      <nav>
        <div class="logo">Brand Name</div>
        <div class="nav-links">
          <a href="#" class="nav-link active">Home</a>
          <a href="#" class="nav-link">Features</a>
          <a href="#" class="nav-link">Pricing</a>
          <a href="#" class="nav-link">About</a>
          <a href="#" class="nav-link">Contact</a>
        </div>
        <button class="button">Sign Up</button>
      </nav>
    </div>
  </header>
  
  <div class="container">
    <section class="hero">
      <h1>Welcome to Our Platform</h1>
      <p>The most comprehensive solution for all your needs. Try it today and see the difference!</p>
      <button class="button">Get Started</button>
    </section>
    
    <section class="features">
      <div class="feature">
        <div class="feature-icon">🚀</div>
        <h3>Fast Performance</h3>
        <p>Our platform is optimized for speed and efficiency, ensuring a smooth experience.</p>
      </div>
      <div class="feature">
        <div class="feature-icon">🔒</div>
        <h3>Secure & Reliable</h3>
        <p>Your data is protected with enterprise-grade security measures.</p>
      </div>
      <div class="feature">
        <div class="feature-icon">💡</div>
        <h3>Innovative Features</h3>
        <p>Stay ahead with cutting-edge tools and functionality.</p>
      </div>
    </section>
    
    <section>
      <h2 style="text-align: center; margin-bottom: 30px;">Contact Us</h2>
      <form>
        <div class="form-group">
          <label for="name">Name</label>
          <input type="text" id="name" placeholder="Enter your name">
        </div>
        <div class="form-group">
          <label for="email">Email</label>
          <input type="email" id="email" placeholder="Enter your email">
        </div>
        <div class="form-group">
          <label for="message">Message</label>
          <textarea id="message" rows="5" placeholder="Enter your message"></textarea>
        </div>
        <button class="button" style="width: 100%;">Submit</button>
      </form>
    </section>
  </div>
  
  <footer>
    <div class="container">
      <p>&copy; 2025 Brand Name. All rights reserved.</p>
    </div>
  </footer>
</body>
</html>`;
  };
  
  // Handle accepting the AI generated content from chat
  const handleAcceptValidations = (validationText) => {
    if (type === 'wireframes') {
      // For wireframes, we'll handle this via the API directly
      // The content is already saved when generated
      setShowAIChat(false);
      
      // Show notification
      showNotification({
        message: "Wireframe accepted and saved",
        type: "success"
      });
    } else if (step === 1) {
      // For validation rules, use the template
      const fullValidationRules = `# Field Validation Rules

${validationText}

## Username
**Type:** string
**Required:** Yes

### Validation Rules
- **Pattern:** ^[a-zA-Z0-9_-]{3,16}$
- **Min Length:** 3
- **Max Length:** 16

### Examples
\`user_123\`

---

## PhoneNumber
**Type:** string
**Required:** Yes

### Validation Rules
- **Pattern:** ^\\+?[1-9]\\d{1,14}$
- **Description:** Should follow E.164 format

### Examples
\`+12125551234\`

---

## DateOfBirth
**Type:** date
**Required:** Yes

### Validation Rules
- **Min Value:** 1900-01-01
- **Max Value:** Current date
- **Format:** YYYY-MM-DD

### Examples
\`1990-01-15\`

---`;
      
      // Set content and close chat view
      setContent(fullValidationRules);
      setIsSaved(false);
      setShowAIChat(false);
      
      // Show notification
      showNotification({
        message: "Validation rules generated successfully",
        type: "success"
      });
    } else {
      // For other document types, just use the generated content
      setContent(validationText);
      setIsSaved(false);
      setShowAIChat(false);
      
      // Show notification
      showNotification({
        message: "Content generated and accepted",
        type: "success"
      });
    }
  };

  // Import functionality
  const handleImportOpen = () => {
    setImportDialogOpen(true);
    setActiveStep(0);
    setImportFile(null);
    setImportType('json');
    setImportedContent('');
    setProcessedContent('');
    setImportError(null);
  };

  const handleImportClose = () => {
    setImportDialogOpen(false);
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setImportFile(file);
      // Auto detect file type from extension
      if (file.name.endsWith('.json')) {
        setImportType('json');
      } else if (file.name.endsWith('.csv')) {
        setImportType('csv');
      } else if (file.name.endsWith('.xml')) {
        setImportType('xml');
      } else if (file.name.endsWith('.yaml') || file.name.endsWith('.yml')) {
        setImportType('yaml');
      }
    }
  };

  const handleNextStep = () => {
    if (activeStep === 0 && importFile) {
      // Read file content
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const fileContent = e.target.result;
          setImportedContent(fileContent);
          
          // Process content based on file type
          let processedText = '';
          
          if (importType === 'json') {
            try {
              const jsonData = JSON.parse(fileContent);
              // Process JSON data into validation rules
              if (step === 1) { // Field Validations
                processedText = processJsonToValidations(jsonData);
              } else {
                processedText = JSON.stringify(jsonData, null, 2);
              }
            } catch (err) {
              setImportError('Invalid JSON file. Please check the file format.');
              return;
            }
          } else if (importType === 'csv') {
            // Process CSV data
            if (step === 1) { // Field Validations
              processedText = processCsvToValidations(fileContent);
            } else {
              processedText = fileContent;
            }
          } else {
            processedText = fileContent;
          }
          
          setProcessedContent(processedText);
          setActiveStep(1);
        } catch (err) {
          console.error('Error processing file:', err);
          setImportError(`Error processing file: ${err.message}`);
        }
      };
      
      reader.onerror = () => {
        setImportError('Error reading file');
      };
      
      if (importType === 'json' || importType === 'yaml' || importType === 'xml') {
        reader.readAsText(importFile);
      } else if (importType === 'csv') {
        reader.readAsText(importFile);
      }
    } else {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBackStep = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleUseImportedContent = () => {
    setContent(processedContent);
    setIsSaved(false);
    setImportDialogOpen(false);
    // Switch to Edit tab
    setCurrentTab(0);
    setEditMode(true);
  };

  const handleAskAI = () => {
    // Set the imported content to be displayed in the chat
    setContent(processedContent);
    setIsSaved(false);
    setImportDialogOpen(false);
    // Switch to Chat tab
    setCurrentTab(1);
  };

  // Function to parse validation content and render as accordions
  const renderValidationAccordions = (validationContent) => {
    // Parse the markdown content to extract field validations
    try {
      // Split content by section headers (## Field Name)
      const sections = validationContent.split(/\n##\s+/);
      
      // First section might be the title or intro - skip if it starts with #
      const fieldSections = sections[0].startsWith('# ') ? sections.slice(1) : sections;
      
      if (fieldSections.length === 0) {
        return (
          <Typography color="text.secondary" align="center" sx={{ py: 3 }}>
            No validation rules found in the content
          </Typography>
        );
      }
      
      // Return an accordion for each field
      return (
        <Box>
          {fieldSections.map((section, index) => {
            // Get field name (first line)
            const lines = section.split('\n');
            const fieldName = lines[0].trim();
            
            // Determine field type and icon
            let fieldIcon = <ArticleIcon />;
            if (fieldName.toLowerCase().includes('email')) {
              fieldIcon = <EmailIcon />;
            } else if (fieldName.toLowerCase().includes('password')) {
              fieldIcon = <PasswordIcon />;
            } else if (fieldName.toLowerCase().includes('phone') || fieldName.toLowerCase().includes('mobile')) {
              fieldIcon = <PhoneIcon />;
            } else if (fieldName.toLowerCase().includes('url') || fieldName.toLowerCase().includes('website')) {
              fieldIcon = <UrlIcon />;
            } else if (fieldName.toLowerCase().includes('date')) {
              fieldIcon = <DateIcon />;
            } else if (fieldName.toLowerCase().includes('user') || fieldName.toLowerCase().includes('name')) {
              fieldIcon = <PersonIcon />;
            } else if (fieldName.toLowerCase().includes('domain') || fieldName.toLowerCase().includes('ip')) {
              fieldIcon = <DomainIcon />;
            } else if (fieldName.toLowerCase().includes('number') || fieldName.toLowerCase().includes('amount')) {
              fieldIcon = <NumberIcon />;
            }
            
            // Extract field properties
            const fieldType = section.match(/\*\*Type:\*\*\s+(.*)/i)?.[1] || '';
            const isRequired = section.match(/\*\*Required:\*\*\s+(.*)/i)?.[1] || '';
            
            return (
              <Accordion key={index} sx={{ 
                mb: 1,
                boxShadow: 'none',
                border: '1px solid rgba(0,0,0,0.1)',
                '&:before': {
                  display: 'none',
                }
              }}>
                <AccordionSummary 
                  expandIcon={<ExpandMoreIcon />}
                  sx={{ bgcolor: 'rgba(0,0,0,0.02)' }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ 
                      mr: 1.5, 
                      display: 'flex', 
                      alignItems: 'center',
                      color: 'primary.main'
                    }}>
                      {fieldIcon}
                    </Box>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                        {fieldName}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                        {fieldType && (
                          <Chip 
                            label={fieldType} 
                            size="small" 
                            variant="outlined" 
                            sx={{ height: 22, fontSize: '0.75rem' }}
                          />
                        )}
                        {isRequired && (
                          <Chip 
                            label={isRequired === 'Yes' ? 'Required' : 'Optional'} 
                            size="small" 
                            color={isRequired === 'Yes' ? 'error' : 'default'}
                            variant="outlined"
                            sx={{ height: 22, fontSize: '0.75rem' }}
                          />
                        )}
                      </Box>
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 2 }}>
                  <ReactMarkdown>
                    {section}
                  </ReactMarkdown>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
      );
    } catch (error) {
      console.error('Error parsing validation content:', error);
      return (
        <Typography color="error" sx={{ py: 2 }}>
          Error parsing validation content. Please check the format.
        </Typography>
      );
    }
  };
  
  // Helper function to process JSON into validation rules format
  const processJsonToValidations = (jsonData) => {
    try {
      let markdownContent = '# Field Validation Rules\n\n';
      
      // Handle different JSON structures
      if (Array.isArray(jsonData)) {
        // Assume array of field definitions
        jsonData.forEach((field, index) => {
          const fieldName = field.name || field.field || field.fieldName || `Field ${index + 1}`;
          markdownContent += `## ${fieldName}\n\n`;
          
          if (field.type) markdownContent += `**Type:** ${field.type}\n\n`;
          if (field.required !== undefined) markdownContent += `**Required:** ${field.required ? 'Yes' : 'No'}\n\n`;
          
          // Process validation rules
          if (field.validations || field.rules || field.constraints) {
            const rules = field.validations || field.rules || field.constraints;
            markdownContent += `### Validation Rules\n\n`;
            
            if (Array.isArray(rules)) {
              rules.forEach(rule => {
                if (typeof rule === 'object') {
                  Object.entries(rule).forEach(([key, value]) => {
                    markdownContent += `- **${key}:** ${value}\n`;
                  });
                } else {
                  markdownContent += `- ${rule}\n`;
                }
              });
            } else if (typeof rules === 'object') {
              Object.entries(rules).forEach(([key, value]) => {
                markdownContent += `- **${key}:** ${value}\n`;
              });
            }
            
            markdownContent += '\n';
          }
          
          // Process examples if available
          if (field.example || field.examples) {
            markdownContent += `### Examples\n\n`;
            
            if (field.example) {
              markdownContent += `\`${field.example}\`\n\n`;
            }
            
            if (field.examples && Array.isArray(field.examples)) {
              field.examples.forEach(example => {
                markdownContent += `- \`${example}\`\n`;
              });
              markdownContent += '\n';
            }
          }
          
          markdownContent += '---\n\n';
        });
      } else if (typeof jsonData === 'object') {
        // Assume object with field names as keys
        Object.entries(jsonData).forEach(([fieldName, fieldData]) => {
          markdownContent += `## ${fieldName}\n\n`;
          
          if (fieldData.type) markdownContent += `**Type:** ${fieldData.type}\n\n`;
          if (fieldData.required !== undefined) markdownContent += `**Required:** ${fieldData.required ? 'Yes' : 'No'}\n\n`;
          
          // Process validation rules
          if (fieldData.validations || fieldData.rules || fieldData.constraints) {
            const rules = fieldData.validations || fieldData.rules || fieldData.constraints;
            markdownContent += `### Validation Rules\n\n`;
            
            if (Array.isArray(rules)) {
              rules.forEach(rule => {
                if (typeof rule === 'object') {
                  Object.entries(rule).forEach(([key, value]) => {
                    markdownContent += `- **${key}:** ${value}\n`;
                  });
                } else {
                  markdownContent += `- ${rule}\n`;
                }
              });
            } else if (typeof rules === 'object') {
              Object.entries(rules).forEach(([key, value]) => {
                markdownContent += `- **${key}:** ${value}\n`;
              });
            }
            
            markdownContent += '\n';
          }
          
          // Process examples if available
          if (fieldData.example || fieldData.examples) {
            markdownContent += `### Examples\n\n`;
            
            if (fieldData.example) {
              markdownContent += `\`${fieldData.example}\`\n\n`;
            }
            
            if (fieldData.examples && Array.isArray(fieldData.examples)) {
              fieldData.examples.forEach(example => {
                markdownContent += `- \`${example}\`\n`;
              });
              markdownContent += '\n';
            }
          }
          
          markdownContent += '---\n\n';
        });
      } else {
        markdownContent += 'Unable to parse JSON structure.\n\n';
      }
      
      return markdownContent;
    } catch (err) {
      console.error('Error processing JSON:', err);
      return `Error processing JSON: ${err.message}\n\nRaw content:\n\n\`\`\`json\n${JSON.stringify(jsonData, null, 2)}\n\`\`\``;
    }
  };

  // Helper function to process CSV into validation rules format
  const processCsvToValidations = (csvContent) => {
    try {
      const lines = csvContent.split('\n').filter(line => line.trim());
      if (lines.length < 2) {
        return 'CSV file does not contain enough data';
      }
      
      // Parse headers
      const headers = lines[0].split(',').map(h => h.trim());
      
      let markdownContent = '# Field Validation Rules\n\n';
      
      // Process each row
      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(',').map(v => v.trim());
        
        if (values.length < headers.length) continue;
        
        // Create an object from headers and values
        const rowData = {};
        headers.forEach((header, index) => {
          rowData[header] = values[index];
        });
        
        const fieldName = rowData.name || rowData.field || rowData.fieldName || rowData.Field || `Field ${i}`;
        markdownContent += `## ${fieldName}\n\n`;
        
        // Add field properties
        Object.entries(rowData).forEach(([key, value]) => {
          if (key.toLowerCase() !== 'name' && 
              key.toLowerCase() !== 'field' && 
              key.toLowerCase() !== 'fieldname') {
            if (key.toLowerCase() === 'required') {
              markdownContent += `**${key}:** ${value.toLowerCase() === 'true' || value === '1' ? 'Yes' : 'No'}\n\n`;
            } else if (key.toLowerCase() === 'validations' || key.toLowerCase() === 'rules') {
              markdownContent += `### Validation Rules\n\n${value}\n\n`;
            } else if (key.toLowerCase() === 'example' || key.toLowerCase() === 'examples') {
              markdownContent += `### Examples\n\n\`${value}\`\n\n`;
            } else {
              markdownContent += `**${key}:** ${value}\n\n`;
            }
          }
        });
        
        markdownContent += '---\n\n';
      }
      
      return markdownContent;
    } catch (err) {
      console.error('Error processing CSV:', err);
      return `Error processing CSV: ${err.message}\n\nRaw content:\n\n\`\`\`\n${csvContent}\n\`\`\``;
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#f5f7fa' }}>
      {/* Header styled like Create Project page */}
      <Box
        sx={{
          px: 6,
          pt: 3,
          pb: 0,
          width: '100%'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            mb: 3,
            pl: 5,
            pr: 5,
            py: '20px',
            bgcolor: 'white',
            borderRadius: '4px',
            width: '100%',
            boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
            maxWidth: 1200,
            mx: 'auto'
          }}
        >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton edge="start" color="inherit" onClick={handleClose} sx={{ mr: 2 }}>
              <ArrowBackIcon />
            </IconButton>
            
            <Typography 
              variant="h5" 
              component="h1" 
              fontWeight={600}
              sx={{ display: 'flex', alignItems: 'center' }}
            >
              <Box component="span" sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                {step === 1 && <ArticleIcon color="primary" sx={{ mr: 1 }} />}
                {type === 'wireframes' && <ArticleIcon color="primary" sx={{ mr: 1 }} />}
              </Box>
              {getStepInfo(step, type).title}
            </Typography>
          </Box>
          
          <IconButton edge="end" color="inherit" onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>
      </Box>
      
      {/* Main Content */}
      <Box sx={{ flexGrow: 1, overflow: 'auto', px: 6, pb: 4 }}>
        <Box sx={{ maxWidth: 1200, mx: 'auto', width: '100%', height: '100%' }}>
          {localLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" height="100%">
              <CircularProgress />
            </Box>
          ) : localError ? (
            <Alert severity="error" sx={{ m: 3 }}>
              {localError}
            </Alert>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%' }}>
            {/* Header with plus button */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              py: 1.5,
              px: 3,
              borderBottom: '1px solid rgba(0,0,0,0.1)',
              bgcolor: '#fff'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', py: 3}}>
                <Typography sx={{ fontWeight: 500 }}>
                  {type === 'wireframes' ? 'Wireframes' : step === 1 ? 'Field Validation Rules' : type === 'build' ? 'Build' : getStepInfo(step, type).title}
                </Typography>
                {!content && type !== 'build' && (
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                    (None added yet)
                  </Typography>
                )}
              </Box>
              
              {/* Quality and Plus buttons */}
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Tooltip title="Check quality" arrow>
                  <IconButton 
                    onClick={() => setActiveTab('quality')}
                    size="small"
                    sx={{ 
                      bgcolor: 'action.hover',
                      transition: 'all 0.3s ease',
                      p: 2,
                      mr: 2,
                      '&:hover': {
                        bgcolor: 'action.selected',
                        transform: 'scale(1.1)',
                        boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                      }
                    }}
                  >
                    <QualityIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Add content" arrow>
                  <IconButton 
                    onClick={(e) => setMenuAnchorEl(e.currentTarget)}
                    size="small"
                    sx={{ 
                      bgcolor: 'action.hover',
                      transition: 'all 0.3s ease',
                      p: 2,
                      '&:hover': {
                        bgcolor: 'action.selected',
                        transform: 'scale(1.1)',
                        boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                      }
                    }}
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                
                {/* Menu for add button */}
                <Menu
                  anchorEl={menuAnchorEl}
                  open={Boolean(menuAnchorEl)}
                  onClose={() => setMenuAnchorEl(null)}
                  PaperProps={{
                    elevation: 3,
                    sx: { mt: 1, width: 240 }
                  }}
                >
                  <MenuItem onClick={() => {
                    setShowDefaultValidationsDialog(true);
                    setMenuAnchorEl(null);
                  }}>
                    <ArticleIcon sx={{ mr: 1, fontSize: 20 }} />
                    <Typography variant="body2">Load default validations</Typography>
                  </MenuItem>
                  
                  <MenuItem onClick={() => {
                    handleImportOpen();
                    setMenuAnchorEl(null);
                  }}>
                    <UploadFileIcon sx={{ mr: 1, fontSize: 20 }} />
                    <Typography variant="body2">Import validations</Typography>
                  </MenuItem>
                  
                  <MenuItem onClick={() => {
                    // Toggle AI chat view directly instead of opening a dialog
                    setShowAIChat(true);
                    setMenuAnchorEl(null);
                  }}>
                    <ChatIcon sx={{ mr: 1, fontSize: 20 }} />
                    <Typography variant="body2">Generate with AI</Typography>
                  </MenuItem>
                </Menu>
              </Box>
            </Box>
            
            {/* Show chat or validation content based on state */}
            {showAIChat ? (
              // Chat interface with split view for wireframe preview
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                height: 'calc(100vh - 120px)', 
                overflow: 'hidden'
              }}>
                {/* Split view for chat and wireframe preview */}
                <Box sx={{ 
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  flexGrow: 1,
                  overflow: 'hidden',
                  position: 'relative',
                  width: '100%',
                  height: '100%'
                }}>
                  {/* Messages area - left side */}
                  <Box sx={{ 
                    flexGrow: 1, 
                    flexBasis: type === 'wireframes' ? '50%' : '100%',
                    overflow: 'auto', 
                    p: 3, 
                    bgcolor: 'white',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRight: type === 'wireframes' ? { xs: 'none', md: '1px solid rgba(0,0,0,0.1)' } : 'none'
                  }}>
                  {/* Show initial greeting with avatar/name */}
                  <Box sx={{ mb: 0.5, display: "flex", alignItems: "center" }}>
                    <Box 
                      sx={{ 
                        width: 28,
                        height: 28,
                        mr: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '50%',
                        backgroundColor: 'transparent',
                        overflow: 'hidden'
                      }}
                    >
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: 'text.secondary',
                          fontWeight: 600,
                          fontSize: '0.8rem'
                        }}
                      >
                        A
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" fontWeight={500}>
                      AI Assistant
                    </Typography>
                  </Box>
                  
                  <Box sx={{ pl: 5, width: '100%' }}>
                    <Box sx={{ 
                      bgcolor: 'white', 
                      p: 3, 
                      borderRadius: '12px', 
                      mb: 2, 
                      maxWidth: '95%',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                    }}>
                      <Typography variant="body1">
                        I can help you create field validation rules for your application. What types of fields do you need to validate?
                      </Typography>
                    </Box>
                  </Box>

                  {/* Chat history with proper avatar/styling like Conversation.js */}
                  {chatHistory.map((msg, index) => (
                    <Box key={index} sx={{ mb: 3 }}>
                      <Box mb={0.5} display="flex" alignItems="center">
                        <Box 
                          sx={{ 
                            width: 28,
                            height: 28,
                            mr: 1.5,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: '50%',
                            backgroundColor: msg.role === 'user' ? 'rgba(25, 118, 210, 0.1)' : 'transparent',
                            overflow: 'hidden'
                          }}
                        >
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: msg.role === 'user' ? 'primary.main' : 'text.secondary',
                              fontWeight: 600,
                              fontSize: '0.8rem'
                            }}
                          >
                            {msg.role === 'user' ? 'Y' : 'A'}
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary" fontWeight={500}>
                          {msg.role === 'user' ? (msg.createdByName || msg.created_by_name || 'You') : 'AI Assistant'}
                        </Typography>
                      </Box>
                      <Box sx={{ 
                        pl: 5,
                        pr: msg.role === 'user' ? 2 : 0,
                        width: '100%'
                      }}>
                        <Box sx={{ 
                          p: 2, 
                          borderRadius: '12px',
                          backgroundColor: msg.role === 'user' ? 'rgba(234, 240, 246, 1)' : 
                                          (msg.isThinking ? 'rgba(244, 250, 244, 0.9)' : 'white'), 
                          color: 'text.primary',
                          wordBreak: 'break-word',
                          boxShadow: 'none',
                          border: msg.role === 'user' ? '1px solid rgba(200, 210, 220, 0.8)' : 
                                  (msg.isThinking ? '1px solid rgba(76, 175, 80, 0.3)' : 'none')
                        }}>
                          {msg.isThinking ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <CircularProgress size={16} sx={{ mr: 1 }} />
                                <Typography variant="body2" color="text.secondary">
                                  {msg.content}
                                </Typography>
                              </Box>
                              {msg.progress !== undefined && (
                                <Box sx={{ width: '100%', mt: 1 }}>
                                  <LinearProgress 
                                    variant="determinate" 
                                    value={msg.progress} 
                                    sx={{ 
                                      height: 6, 
                                      borderRadius: 3,
                                      bgcolor: 'rgba(76, 175, 80, 0.1)',
                                      '& .MuiLinearProgress-bar': {
                                        bgcolor: 'rgba(76, 175, 80, 0.8)'
                                      }
                                    }}
                                  />
                                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', textAlign: 'right' }}>
                                    {msg.progress}% complete
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          ) : msg.role === 'assistant' ? (
                            <Box 
                              sx={{ 
                                '& h1, & h2, & h3, & h4, & h5, & h6': {
                                  fontWeight: 600,
                                  marginTop: 2,
                                  marginBottom: 1,
                                },
                                '& h1': { fontSize: '1.5rem' },
                                '& h2': { fontSize: '1.3rem' },
                                '& h3': { fontSize: '1.1rem' },
                                '& p': { marginBottom: 1.5 },
                                '& ul, & ol': { paddingLeft: 3, marginBottom: 1.5 },
                                '& li': { marginBottom: 0.5 },
                                '& code': {
                                  backgroundColor: '#f5f5f5',
                                  padding: '2px 4px',
                                  borderRadius: 4,
                                  fontSize: '0.9em',
                                }
                              }}
                            >
                              <ReactMarkdown>{msg.content}</ReactMarkdown>
                            </Box>
                          ) : (
                            <Typography variant="body1">
                              {msg.content}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  ))}
                  
                  {/* Typing indicator */}
                  {isChatTyping && (
                    <Box sx={{ mb: 3 }}>
                      <Box sx={{ mb: 0.5, display: "flex", alignItems: "center" }}>
                        <Box 
                          sx={{ 
                            width: 28,
                            height: 28,
                            mr: 1.5,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: '50%',
                            backgroundColor: 'transparent',
                            overflow: 'hidden'
                          }}
                        >
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: 'text.secondary',
                              fontWeight: 600,
                              fontSize: '0.8rem'
                            }}
                          >
                            A
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary" fontWeight={500}>
                          AI Assistant
                        </Typography>
                      </Box>
                      
                      <Box sx={{ pl: 5, width: '100%' }}>
                        <Box sx={{ 
                          bgcolor: 'rgba(244, 250, 244, 0.9)', 
                          p: 3, 
                          borderRadius: '12px', 
                          mb: 2, 
                          maxWidth: '95%',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                          border: '1px solid rgba(76, 175, 80, 0.3)'
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <CircularProgress size={16} sx={{ mr: 1 }} />
                            <Typography variant="body2" color="text.secondary">
                              AI Assistant is thinking...
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  )}
                  </Box>
                  
                  {/* Wireframe preview area - right side - always show when type is wireframes */}
                  {type === 'wireframes' && (
                    <Box sx={{ 
                      flexBasis: '50%',
                      width: { xs: '100%', md: '50%' },
                      overflow: 'auto',
                      bgcolor: 'white',
                      display: 'block',
                      position: 'relative',
                      height: '100%'
                    }}>
                      {/* Show iframe for any content - it will show the streaming animation too */}
                      <Box sx={{ p: 0, height: '100%', width: '100%', position: 'relative' }}>
                        <Box sx={{ 
                          position: 'absolute', 
                          top: 0, 
                          left: 0, 
                          right: 0, 
                          p: 1, 
                          backgroundColor: 'rgba(255,255,255,0.9)', 
                          borderBottom: '1px solid rgba(0,0,0,0.1)',
                          zIndex: 2,
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}>
                          <Typography variant="body2" fontWeight={500} sx={{ display: 'flex', alignItems: 'center' }}>
                            <ArticleIcon fontSize="small" sx={{ mr: 0.5 }} />
                            {content ? "Wireframe Preview" : "Preparing Wireframe..."}
                          </Typography>
                          {content && (
                            <IconButton size="small" onClick={() => setContent('')}>
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                        
                        {content ? (
                          <iframe 
                            srcDoc={content}
                            title="Wireframe Preview"
                            style={{ 
                              width: '100%', 
                              height: 'calc(100% - 32px)', // Adjusted height to account for header
                              border: 'none',
                              background: 'white',
                              marginTop: '32px', // Space for the header
                              display: 'block' // Ensure it's displayed properly
                            }}
                            sandbox="allow-scripts allow-same-origin"
                          />
                        ) : (
                          <Box sx={{ 
                            display: 'flex', 
                            flexDirection: 'column',
                            alignItems: 'center', 
                            justifyContent: 'center',
                            height: '100%',
                            p: 3,
                            marginTop: '32px' // Space for the header
                          }}>
                            <CircularProgress size={40} sx={{ mb: 2 }} />
                            <Typography variant="body1" align="center">
                              Waiting for wireframe generation...
                            </Typography>
                            <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
                              The wireframe will appear here when you start generating
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  )}
                </Box>
                
                {/* Input area with same style as Conversation.js */}
                <Box sx={{ 
                  borderTop: '1px solid rgba(0,0,0,0.1)', 
                  padding: 2,
                  paddingLeft: 6,
                  paddingRight: 6,
                  bgcolor: 'white',
                  display: 'flex', 
                  flexDirection: 'column',
                  alignItems: 'center'
                }}>
                  <Box sx={{ 
                    maxWidth: '1200px', 
                    width: '100%', 
                    display: 'flex', 
                    flexDirection: 'column' 
                  }}>
                    {/* Action buttons row */}
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      mb: 1,
                      borderRadius: '20px',
                      backgroundColor: 'rgba(0,0,0,0.03)',
                      p: 0.75,
                      px: 1.5
                    }}>
                      {/* Voice input button */}
                      <IconButton size="small" sx={{ mr: 1 }}>
                        <MicIcon fontSize="small" />
                      </IconButton>
                      
                      {/* Language button */}
                      <IconButton size="small" sx={{ mr: 1 }}>
                        <LanguageIcon fontSize="small" />
                      </IconButton>
                      
                      {/* File upload button */}
                      <Box 
                        component="span" 
                        sx={{ 
                          position: 'relative',
                          display: 'inline-block' 
                        }}
                        onDragEnter={handleDragEnter}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                      >
                        <IconButton 
                          component="label" 
                          size="small" 
                          sx={{ 
                            mr: 1,
                            color: isDragging ? 'primary.main' : 'inherit',
                            bgcolor: isDragging ? 'rgba(25, 118, 210, 0.08)' : 'transparent'
                          }}
                        >
                          <AttachFileIcon fontSize="small" />
                          <input 
                            type="file" 
                            hidden 
                            multiple 
                            onChange={handleFileUpload}
                          />
                        </IconButton>
                      </Box>

                      {/* Display file count badge when files are attached */}
                      {wireframeAttachments.length > 0 && (
                        <Chip
                          size="small"
                          label={`${wireframeAttachments.length} file(s)`}
                          color="primary"
                          variant="outlined"
                          sx={{ mr: 1, height: 24 }}
                        />
                      )}
                    </Box>

                    {/* Display attached files when present */}
                    {wireframeAttachments.length > 0 && (
                      <Box sx={{ 
                        mb: 2, 
                        p: 1, 
                        border: '1px dashed rgba(0,0,0,0.2)',
                        borderRadius: 1,
                        bgcolor: 'rgba(0,0,0,0.02)'
                      }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                          Attachments for wireframe generation:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {wireframeAttachments.map((attachment, index) => (
                            <Chip
                              key={index}
                              label={attachment.name}
                              size="small"
                              variant="outlined"
                              onDelete={() => handleRemoveAttachment(index)}
                              icon={
                                attachment.type.startsWith('image/') ? (
                                  <ArticleIcon fontSize="small" />
                                ) : (
                                  <AttachFileIcon fontSize="small" />
                                )
                              }
                            />
                          ))}
                        </Box>
                        {type === 'wireframes' && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                            These files will be used as references when generating your wireframe.
                          </Typography>
                        )}
                      </Box>
                    )}
                    
                    {/* Input field and send button row */}
                    <Grid container spacing={1} alignItems="flex-end">
                      <Grid item xs>
                        <TextField
                          fullWidth
                          placeholder={type === 'wireframes' ? 
                            "Describe the wireframe you want to generate..." : 
                            "Chat..."}
                          variant="outlined"
                          size="medium"
                          value={chatMessage}
                          onChange={(e) => setChatMessage(e.target.value)}
                          multiline
                          maxRows={4}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              handleSendMessage();
                            }
                          }}
                        />
                      </Grid>
                      <Grid item sx={{ mb: 1 }}>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={handleSendMessage}
                          disabled={!chatMessage.trim() || isChatTyping}
                          sx={{ 
                            height: '40px',
                            minWidth: '40px',
                            width: '40px',
                            borderRadius: '50%',
                            padding: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <SendIcon />
                        </Button>
                      </Grid>
                    </Grid>
                  </Box>
                </Box>
                
                {/* Back button to return to validations view */}
                <Box sx={{ 
                  p: 2, 
                  borderTop: '1px solid rgba(0,0,0,0.1)', 
                  bgcolor: '#f8f9fa',
                  display: 'flex', 
                  justifyContent: 'flex-start'
                }}>
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => {
                      // Check if we need to accept and use the generated validations
                      if (chatHistory.length > 0) {
                        const lastAssistantMessage = chatHistory.filter(m => m.role === 'assistant').pop();
                        if (lastAssistantMessage) {
                          handleAcceptValidations(lastAssistantMessage.content);
                        } else {
                          setShowAIChat(false);
                        }
                      } else {
                        setShowAIChat(false);
                      }
                    }}
                  >
                    Use Generated Rules & Return
                  </Button>
                  
                  <Button
                    variant="text"
                    color="inherit"
                    onClick={() => setShowAIChat(false)}
                    sx={{ ml: 2 }}
                  >
                    Cancel
                  </Button>
                </Box>
              </Box>
            ) : (
              // Content display based on activeTab and type
              <Box 
              sx={{ 
                overflow: 'auto', 
                height: 'calc(100vh - 280px)', 
                backgroundColor: '#fff',           
                boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: 'rgba(0,0,0,0.05)',
                  borderRadius: '8px',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: 'rgba(0,0,0,0.2)',
                  borderRadius: '8px',
                  '&:hover': {
                    backgroundColor: 'rgba(0,0,0,0.3)',
                  },
                },
              }}>
                {activeTab === 'quality' ? (
                  // Quality Analysis page
                  <QualityAnalysis 
                    projectId={projectId}
                    onClose={() => setActiveTab('content')}
                  />
                ) : type === 'user-stories' ? (
                  // User Stories component
                  <UserStories 
                    existingContent={content}
                    onSaveContent={(newContent) => {
                      setContent(newContent);
                      setIsSaved(false);
                    }}
                  />
                ) : type === 'architecture' ? (
                  // Architecture component
                  <Architecture 
                    projectId={projectId}
                    existingContent={content}
                    onSave={(newContent) => {
                      setContent(newContent);
                      setIsSaved(false);
                    }}
                  />
                ) : type === 'field-validation' ? (
                  // Field Validation component
                  <FieldValidation 
                    projectId={projectId}
                    existingContent={content}
                    onSave={(newContent) => {
                      setContent(newContent);
                      setIsSaved(false);
                    }}
                  />
                ) : type === 'dev-guidelines' ? (
                  // Development Guidelines with Workspace Creation
                  <DevGuidelines
                    projectId={projectId}
                    existingContent={content}
                    onSave={(newContent) => {
                      setContent(newContent);
                      setIsSaved(false);
                    }}
                  />
                ) : type === 'tdd-development' ? (
                  // TDD Development Workflow with Ralphio Integration
                  <TDDProvider>
                    <SplitViewTDD projectId={projectId} />
                  </TDDProvider>
                ) : type === 'build' ? (
                  // Bolt-Style Code Development Studio
                  <BoltStyleCodeDevelopment
                    key={`bolt-code-dev-${projectId}`}
                    projectId={projectId}
                    initialTasks={codeTasks}
                    onCodeChange={handleCodeChange}
                    onTaskComplete={handleTaskComplete}
                  />
                ) : type === 'designs' ? (
                  // Designs component
                  <Designs 
                    projectId={projectId}
                    onSave={(newContent) => {
                      setContent(newContent);
                      setIsSaved(false);
                    }}
                  />
                ) : type === 'techStack' ? (
                  // Technology Stack component
                  <TechnologyStack 
                    projectId={projectId}
                    existingContent={content}
                    onSave={(newContent) => {
                      setContent(newContent);
                      setIsSaved(false);
                    }}
                  />
                ) : type === 'wireframes-component' ? (
                  // Use Wireframes component for editing
                  <Wireframes
                    projectId={projectId}
                    onSave={(newContent) => {
                      setContent(newContent);
                      setIsSaved(false);
                    }}
                  />
                ) : type === 'wireframes' ? (
                  // Use Wireframes component directly
                  <Wireframes
                    projectId={projectId}
                    onSave={(newContent) => {
                      setContent(newContent);
                      setIsSaved(false);
                    }}
                  />
                ) : type === 'wireframes-preview' && content ? (
                  // Wireframe HTML preview
                  <Box>
                    <Box sx={{ p: 2, mb: 2, bgcolor: 'white', border: '1px solid rgba(0,0,0,0.1)', borderRadius: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="body1" fontWeight={500}>
                          AI-Generated Wireframe Preview
                        </Typography>
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          startIcon={<SaveIcon />}
                          onClick={async () => {
                            try {
                              // Show loading indicator
                              setLocalLoading(true);
                              
                              // Import document service
                              const documentService = await import('../services/documentService').then(module => module.default);
                              
                              // Save the wireframe
                              const savedDoc = await documentService.saveWireframe(
                                projectId, 
                                content, 
                                'AI Generated Wireframe', 
                                window.wireframeKey || `wireframe-${Date.now()}-${Math.floor(Math.random() * 1000)}`
                              );
                              
                              // Update saved status and show success notification
                              setIsSaved(true);
                              
                              if (savedDoc && savedDoc._id) {
                                setDocumentId(savedDoc._id);
                              }
                              
                              // Show success notification
                              showNotification({
                                message: 'Wireframe saved successfully!',
                                type: 'success'
                              });
                              
                              // No need to refresh documents as this is handled by the parent component
                            } catch (error) {
                              console.error('Error saving wireframe:', error);
                              
                              // Show error notification
                              showNotification({
                                message: `Error: ${error.message || 'Failed to save wireframe'}`,
                                type: 'error'
                              });
                            } finally {
                              setLocalLoading(false);
                            }
                          }}
                        >
                          Save Wireframe
                        </Button>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        The wireframe has been generated and rendered below. Click "Save Wireframe" to save it to your project.
                      </Typography>
                    </Box>
                    <Box sx={{ 
                      height: 'calc(100vh - 300px)', 
                      width: '100%', 
                      border: '1px solid rgba(0,0,0,0.1)', 
                      mb: 2,
                      position: 'relative',
                      bgcolor: 'white'
                    }}>
                      {content.startsWith('<!DOCTYPE html>') || content.startsWith('<html') ? (
                        <iframe 
                          srcDoc={content}
                          title="Wireframe Preview"
                          style={{ 
                            width: '100%', 
                            height: '100%',
                            border: 'none',
                            background: 'white'
                          }}
                          sandbox="allow-scripts allow-same-origin"
                          onLoad={() => {
                            // Add any iframe onload handling here
                            console.log('Wireframe iframe loaded successfully');
                          }}
                        />
                      ) : (
                        <Box sx={{ 
                          display: 'flex', 
                          flexDirection: 'column',
                          alignItems: 'center', 
                          justifyContent: 'center',
                          height: '100%',
                          p: 3
                        }}>
                          <CircularProgress size={40} sx={{ mb: 2 }} />
                          <Typography variant="body1" align="center">
                            Processing wireframe HTML...
                          </Typography>
                          <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
                            The HTML content is being prepared for display
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                ) : content ? (
                  <Box>
                    {/* Parse content and display as accordions for validation type */}
                    {step === 1 ? renderValidationAccordions(content) : (
                      <Paper variant="outlined" sx={{ p: 2 }}>
                        <ReactMarkdown>{content}</ReactMarkdown>
                      </Paper>
                    )}
                  </Box>
                ) : (
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    py: 8,
                    textAlign: 'center',
                    height: '100%'
                  }}>
                    {type === 'wireframes' ? (
                      <>
                        <ArticleIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
                        <Typography variant="body1" color="text.secondary">
                          No wireframes added yet
                        </Typography>
                        <Typography variant="body2" color="text.disabled" sx={{ mt: 1, maxWidth: 400 }}>
                          Click the + button to import wireframes or generate with AI
                        </Typography>
                        
                        {/* Quick generate with AI for wireframes */}
                        <Box sx={{ mt: 4, width: '100%', maxWidth: 600 }}>
                          <Paper
                            elevation={0}
                            sx={{ 
                              p: 3, 
                              borderRadius: 2,
                              bgcolor: 'rgba(25, 118, 210, 0.04)',
                              border: '1px solid rgba(25, 118, 210, 0.2)'
                            }}
                          >
                            <Typography variant="subtitle1" fontWeight={500} mb={2}>
                              Generate wireframes with AI
                            </Typography>
                            
                            {/* File upload area with drag and drop */}
                            <Box
                              sx={{
                                border: isDragging ? '2px dashed rgba(25, 118, 210, 0.5)' : '1px dashed rgba(0,0,0,0.2)',
                                borderRadius: 1,
                                p: 2,
                                mb: 2,
                                textAlign: 'center',
                                cursor: 'pointer',
                                bgcolor: isDragging ? 'rgba(25, 118, 210, 0.08)' : 'background.paper',
                                '&:hover': {
                                  bgcolor: 'rgba(0,0,0,0.01)',
                                },
                                transition: 'all 0.2s ease'
                              }}
                              onClick={() => document.getElementById('wireframe-file-upload').click()}
                              onDragEnter={handleDragEnter}
                              onDragOver={handleDragOver}
                              onDragLeave={handleDragLeave}
                              onDrop={handleDrop}
                            >
                              <input
                                type="file"
                                id="wireframe-file-upload"
                                multiple
                                onChange={handleFileUpload}
                                style={{ display: 'none' }}
                              />
                              <UploadFileIcon 
                                color={isDragging ? "primary" : "primary"} 
                                sx={{ 
                                  fontSize: isDragging ? 40 : 32, 
                                  mb: 1,
                                  transition: 'all 0.2s ease'
                                }} 
                              />
                              <Typography 
                                variant="body2" 
                                color={isDragging ? "primary" : "text.secondary"}
                                sx={{ fontWeight: isDragging ? 500 : 400 }}
                              >
                                {isDragging ? 'Drop files here' : 'Drag & drop reference files or click to upload'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                Images, mockups, or text descriptions accepted
                              </Typography>
                            </Box>
                            
                            {/* Display uploaded files */}
                            {wireframeAttachments.length > 0 && (
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="body2" fontWeight={500} mb={1}>
                                  {wireframeAttachments.length} file(s) attached:
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                  {wireframeAttachments.map((attachment, index) => (
                                    <Chip
                                      key={index}
                                      label={attachment.name}
                                      size="small"
                                      variant="outlined"
                                      onDelete={() => handleRemoveAttachment(index)}
                                      icon={
                                        attachment.type.startsWith('image/') ? (
                                          <ArticleIcon fontSize="small" />
                                        ) : (
                                          <AttachFileIcon fontSize="small" />
                                        )
                                      }
                                    />
                                  ))}
                                </Box>
                              </Box>
                            )}
                            
                            {/* Start AI chat button */}
                            <Button
                              variant="contained"
                              color="primary"
                              fullWidth
                              startIcon={<ChatIcon />}
                              onClick={() => setShowAIChat(true)}
                              sx={{ mt: 1 }}
                            >
                              Start AI Chat to Generate Wireframes
                            </Button>
                          </Paper>
                        </Box>
                      </>
                    ) : type === 'user-stories' ? (
                      <>
                        <PersonIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
                        <Typography variant="body1" color="text.secondary">
                          No user stories added yet
                        </Typography>
                        <Typography variant="body2" color="text.disabled" sx={{ mt: 1, maxWidth: 400 }}>
                          Click the + button to upload user stories or generate them from the project PRD
                        </Typography>
                      </>
                    ) : type === 'architecture' ? (
                      <>
                        <ScienceIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
                        <Typography variant="body1" color="text.secondary">
                          No architecture defined yet
                        </Typography>
                        <Typography variant="body2" color="text.disabled" sx={{ mt: 1, maxWidth: 400 }}>
                          Click the + button to define your system architecture or generate it with AI
                        </Typography>
                      </>
                    ) : type === 'field-validation' ? (
                      <>
                        <DomainIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
                        <Typography variant="body1" color="text.secondary">
                          No field validation rules defined yet
                        </Typography>
                        <Typography variant="body2" color="text.disabled" sx={{ mt: 1, maxWidth: 400 }}>
                          Click the + button to define field validation rules or generate them with AI
                        </Typography>
                      </>
                    ) : (
                      <>
                        <ArticleIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
                        <Typography variant="body1" color="text.secondary">
                          No {step === 1 ? 'field validations' : 'content'} added yet
                        </Typography>
                        <Typography variant="body2" color="text.disabled" sx={{ mt: 1, maxWidth: 400 }}>
                          Click the + button to {step === 1 ? 'add validation rules by loading defaults, importing, or generating with AI' : 'import or generate content with AI'}
                        </Typography>
                      </>
                    )}
                  </Box>
                )}
              </Box>
            )}
            
            
            {/* Bottom action bar with Save button (only shown when content exists and not for user stories) */}
            {content && type !== 'user-stories' && (
              <Box sx={{ 
                p: 2, 
                borderTop: '1px solid rgba(0,0,0,0.1)', 
                bgcolor: '#fff',
                display: 'flex', 
                justifyContent: 'flex-end'
              }}>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={isSaved || localLoading}
                  startIcon={<SaveIcon />}
                  onClick={handleSave}
                  sx={{ 
                    borderRadius: 0  // Remove rounded corners
                  }}
                >
                  Save
                </Button>
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* Generate with AI Dialog */}
      <Dialog
        open={genDialogOpen}
        onClose={() => setGenDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
          }
        }}
      >
        <DialogTitle sx={{ bgcolor: 'success.main', color: 'white', display: 'flex', alignItems: 'center' }}>
          <ChatIcon sx={{ mr: 1 }} />
          Generate Field Validations with AI
        </DialogTitle>
        
        <DialogContent sx={{ p: 3 }}>
          <Typography variant="body1" sx={{ mb: 3 }}>
            Describe the form fields in your application, and AI will generate appropriate validation rules for each field.
          </Typography>
          
          <TextField
            fullWidth
            multiline
            rows={8}
            placeholder="For example: I need validation rules for a user registration form with email, password, username, phone number, date of birth and address fields."
            variant="outlined"
            sx={{ mb: 3 }}
          />
          
          <Typography variant="body2" color="text.secondary">
            The AI assistant will analyze your description and generate appropriate validation rules for each field, including required status, patterns, min/max values, and other constraints.
          </Typography>
        </DialogContent>
        
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setGenDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            color="success"
            startIcon={<ChatIcon />}
            onClick={() => {
              // Simulate AI generation with a timeout
              setLocalLoading(true);
              setGenDialogOpen(false);
              
              setTimeout(() => {
                setContent(`# Field Validation Rules

## Email
**Type:** string
**Required:** Yes

### Validation Rules
- **Pattern:** ^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$
- **Min Length:** 5
- **Max Length:** 254

### Examples
\`user@example.com\`

---

## Password
**Type:** string
**Required:** Yes

### Validation Rules
- **Min Length:** 8
- **Max Length:** 128
- **Pattern:** ^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d)(?=.*[@$!%*?&])[A-Za-z\\d@$!%*?&]{8,}$
- **Description:** Must contain at least one uppercase letter, one lowercase letter, one number, and one special character

### Examples
\`P@ssw0rd!\`

---

## Username
**Type:** string
**Required:** Yes

### Validation Rules
- **Pattern:** ^[a-zA-Z0-9_-]{3,16}$
- **Min Length:** 3
- **Max Length:** 16

### Examples
\`user_123\`

---

## PhoneNumber
**Type:** string
**Required:** Yes

### Validation Rules
- **Pattern:** ^\\+?[1-9]\\d{1,14}$
- **Description:** Should follow E.164 format

### Examples
\`+12125551234\`

---

## DateOfBirth
**Type:** date
**Required:** Yes

### Validation Rules
- **Min Value:** 1900-01-01
- **Max Value:** Current date
- **Format:** YYYY-MM-DD

### Examples
\`1990-01-15\`

---

## Address
**Type:** string
**Required:** Yes

### Validation Rules
- **Min Length:** 5
- **Max Length:** 200

### Examples
\`123 Main St, Anytown, NY 12345\`

---`);
                setLocalLoading(false);
                setIsSaved(false);
                
                showNotification({
                  message: "AI has generated validation rules based on your description",
                  type: "success"
                });
              }, 2000);
            }}
          >
            Generate
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import Dialog */}
      {/* Default Validations Dialog */}
      <Dialog
        open={showDefaultValidationsDialog}
        onClose={() => setShowDefaultValidationsDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
          }
        }}
      >
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', display: 'flex', alignItems: 'center' }}>
          <ArticleIcon sx={{ mr: 1 }} />
          Default Field Validations
        </DialogTitle>
        
        <DialogContent sx={{ py: 3 }}>
          <Typography variant="body1" sx={{ mb: 3 }}>
            Would you like to load a set of common field validations as a starting point? This includes validations for:
          </Typography>
          
          <Box sx={{ mb: 3, pl: 2 }}>
            <Typography component="div" variant="body1">
              • Email addresses
            </Typography>
            <Typography component="div" variant="body1">
              • Passwords (strength requirements)
            </Typography>
            <Typography component="div" variant="body1">
              • Phone numbers
            </Typography>
            <Typography component="div" variant="body1">
              • URLs
            </Typography>
            <Typography component="div" variant="body1">
              • Dates and times
            </Typography>
            <Typography component="div" variant="body1">
              • Numeric ranges
            </Typography>
            <Typography component="div" variant="body1">
              • Text length constraints
            </Typography>
          </Box>
          
          <Typography variant="body2" color="text.secondary">
            You can edit these defaults after loading them to fit your specific project requirements.
          </Typography>
        </DialogContent>
        
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setShowDefaultValidationsDialog(false)}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={() => {
              const defaultValidations = `# Field Validation Rules

## Email
**Type:** string
**Required:** Yes

### Validation Rules
- **Pattern:** ^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$
- **Min Length:** 5
- **Max Length:** 254

### Examples
\`user@example.com\`

---

## Password
**Type:** string
**Required:** Yes

### Validation Rules
- **Min Length:** 8
- **Max Length:** 128
- **Pattern:** ^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d)(?=.*[@$!%*?&])[A-Za-z\\d@$!%*?&]{8,}$
- **Description:** Must contain at least one uppercase letter, one lowercase letter, one number, and one special character

### Examples
\`P@ssw0rd!\`

---

## PhoneNumber
**Type:** string
**Required:** Yes

### Validation Rules
- **Pattern:** ^\\+?[1-9]\\d{1,14}$
- **Description:** Should follow E.164 format

### Examples
\`+12125551234\`

---

## URL
**Type:** string
**Required:** No

### Validation Rules
- **Pattern:** ^(https?:\\/\\/)?([\\da-z.-]+)\\.([a-z.]{2,6})([/\\w .-]*)*\\/?$

### Examples
\`https://example.com\`

---

## DateOfBirth
**Type:** date
**Required:** Yes

### Validation Rules
- **Min Value:** 1900-01-01
- **Max Value:** Current date
- **Format:** YYYY-MM-DD

### Examples
\`1990-01-15\`

---

## Age
**Type:** number
**Required:** No

### Validation Rules
- **Min Value:** 0
- **Max Value:** 150
- **Integer:** Yes

---

## Username
**Type:** string
**Required:** Yes

### Validation Rules
- **Pattern:** ^[a-zA-Z0-9_-]{3,16}$
- **Min Length:** 3
- **Max Length:** 16

### Examples
\`user_123\`

---

## ZipCode
**Type:** string
**Required:** Yes

### Validation Rules
- **Pattern:** ^\\d{5}(-\\d{4})?$

### Examples
\`12345\`
\`12345-6789\`

---

## CreditCardNumber
**Type:** string
**Required:** No

### Validation Rules
- **Pattern:** ^(?:4[0-9]{12}(?:[0-9]{3})?|5[1-5][0-9]{14}|3[47][0-9]{13}|3(?:0[0-5]|[68][0-9])[0-9]{11}|6(?:011|5[0-9]{2})[0-9]{12}|(?:2131|1800|35\\d{3})\\d{11})$
- **Luhn Algorithm:** Yes

### Examples
\`4111111111111111\`

---

## IPAddress
**Type:** string
**Required:** No

### Validation Rules
- **Pattern:** ^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$

### Examples
\`192.168.1.1\`

---`;
              
              setContent(defaultValidations);
              setIsSaved(false);
              setShowDefaultValidationsDialog(false);
              setCurrentTab(2); // Switch to Edit tab
              setEditMode(false); // Show preview first
            }}
          >
            Load Default Validations
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import Dialog */}
      <Dialog 
        open={importDialogOpen} 
        onClose={handleImportClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
          }
        }}
      >
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', display: 'flex', alignItems: 'center' }}>
          <UploadFileIcon sx={{ mr: 1 }} />
          Import {contentTitle} Data
        </DialogTitle>
        
        <DialogContent>
          <Stepper activeStep={activeStep} orientation="vertical" sx={{ mt: 2 }}>
            <Step>
              <StepLabel>Select file to import</StepLabel>
              <StepContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Upload a file containing field validation rules. We support JSON, CSV, YAML and XML formats.
                </Typography>
                
                <Box sx={{ mb: 3 }}>
                  <Button
                    component="label"
                    variant="contained"
                    startIcon={<UploadIcon />}
                  >
                    Select File
                    <VisuallyHiddenInput 
                      type="file" 
                      accept=".json,.csv,.xml,.yaml,.yml"
                      onChange={handleFileChange}
                      ref={fileInputRef}
                    />
                  </Button>
                  
                  {importFile && (
                    <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
                      <ArticleIcon color="primary" sx={{ mr: 1 }} />
                      <Typography variant="body2">
                        {importFile.name} ({Math.round(importFile.size / 1024)} KB)
                      </Typography>
                    </Box>
                  )}
                </Box>
                
                <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel id="import-type-label">File Format</InputLabel>
                  <Select
                    labelId="import-type-label"
                    value={importType}
                    label="File Format"
                    onChange={(e) => setImportType(e.target.value)}
                  >
                    <MenuItem value="json">JSON</MenuItem>
                    <MenuItem value="csv">CSV</MenuItem>
                    <MenuItem value="xml">XML</MenuItem>
                    <MenuItem value="yaml">YAML</MenuItem>
                  </Select>
                </FormControl>
                
                {importError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {importError}
                  </Alert>
                )}
              </StepContent>
            </Step>
            
            <Step>
              <StepLabel>Review converted data</StepLabel>
              <StepContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  We've processed your file into a format that works with the system. 
                  Review the content below and decide if you want to:
                </Typography>
                
                <Box sx={{ 
                  border: '1px solid #e0e0e0', 
                  borderRadius: 1, 
                  p: 2, 
                  mb: 3,
                  maxHeight: '300px',
                  overflow: 'auto',
                  bgcolor: '#f9f9f9'
                }}>
                  <ReactMarkdown>
                    {processedContent}
                  </ReactMarkdown>
                </Box>
                
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  You can either use this content directly in the editor, or ask the AI assistant to help you refine it.
                </Typography>
              </StepContent>
            </Step>
          </Stepper>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, borderTop: '1px solid #e0e0e0' }}>
          {activeStep === 0 ? (
            <>
              <Button onClick={handleImportClose}>Cancel</Button>
              <Button 
                variant="contained" 
                onClick={handleNextStep}
                disabled={!importFile}
              >
                Next
              </Button>
            </>
          ) : (
            <>
              <Button onClick={handleBackStep}>Back</Button>
              <Button onClick={handleImportClose}>Cancel</Button>
              <Button 
                variant="outlined" 
                color="primary"
                onClick={handleAskAI}
                startIcon={<ChatIcon />}
              >
                Ask AI to Help
              </Button>
              <Button 
                variant="contained" 
                onClick={handleUseImportedContent}
                startIcon={<EditIcon />}
              >
                Edit Content
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
    </Box>
  );
};

export default PRDWorkflowEdit;